import { useQuery } from "@vue/apollo-composable";
import { parseGqlResponse } from "@/shared/utils/graphql/responseParser";
import {
  AccountPermission,
  AccountPermissionVariables,
  AccountPermission_accountPermission_AccountPermissionDetailList,
} from "@/api/cryptoExchange/__generated__/AccountPermission";
import { accountPermissionGql } from "@/api/cryptoExchange/accountPermission";
import { computed, ref, watch } from "vue";
import { AccountProducts } from "@/../__generated__/globalTypes";
import includes from "lodash/includes";
import isEmpty from "lodash/isEmpty";
import union from "lodash/union";
import { useStore } from "vuex";
import { InternalVerificationStatus } from "@/../__generated__/globalTypes";

// Global state. Set to true once we see the user is verified
const isQueryEnabled = ref(false);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useAccountPermissionQuery() {
  const store = useStore();

  /**
   * Watch store `internalVerificationStatus` if VERIFIED. Enable query once detected.
   *
   * Note: this does not set to false again, as `internalVerificationStatus`
   * is sometimes back to `null` again for some reason.
   */
  watch(
    [() => store?.state?.userInfo?.user?.internalVerificationStatus],
    () => {
      if (
        store?.state?.userInfo?.user?.internalVerificationStatus ===
        InternalVerificationStatus.VERIFIED
      ) {
        isQueryEnabled.value = true;
      }
    },
    { immediate: true }
  );

  /** Only enable query calls if user is verified, otherwise BE will throw an error `NotVerified` */
  const accountPermissionQuery = useQuery<
    AccountPermission,
    AccountPermissionVariables
  >(accountPermissionGql, { filter: {} }, () => ({
    notifyOnNetworkStatusChange: true,
    enabled: isQueryEnabled.value,
  }));

  const accountPermission = computed(() => {
    const paymentOrderGqlResponse =
      parseGqlResponse<AccountPermission_accountPermission_AccountPermissionDetailList>(
        "AccountPermissionDetailList",
        accountPermissionQuery.result.value
      );
    return paymentOrderGqlResponse?.data;
  });

  const fetchAccountPermission = async () => {
    await accountPermissionQuery.refetch();
  };

  const isCryptoWithdrawalPermitted = ref(false);
  const isCryptoDepositPermitted = ref(false);
  const allowedFiatCurrenciesForCryptoWithdrawal = ref();
  const allowedFiatCurrenciesForCryptoDeposit = ref();
  const isRussianBankTransferPermitted = ref(false);
  const isRussianCardTransferPermitted = ref(false);
  const isUkraineBankTransferPermitted = ref(false);
  const isUkraineCardTransferPermitted = ref(false);
  const isIsraelBankTransferPermitted = ref(false);
  const isIsraelCardTransferPermitted = ref(false);
  const isCardAcquiringPermitted = ref(false);
  const allowedFiatCurrenciesForCardAcquiring = ref();
  const isFiatExchangePermitted = ref(false);
  const allowedFiatCurrencyForFiatExchange = ref<string[]>([]);
  // TODO: Update Topup related variables when necessary
  const isAllowedEEA = ref(false); // For local EUR
  const isAllowedLocalGBP = ref(false); // For local GBP
  const isAllowedInternational = ref(false);
  const allowedCurrencyForInternationalTopUp = ref<string[]>([]);
  const allowedCurrencyForCardTransfer = ref<string[]>([]);

  watch([accountPermission], async () => {
    accountPermission.value?.details?.forEach((detail) => {
      switch (detail?.productName) {
        case AccountProducts.OUTWARD_WIRE:
          // Specific values for Russian Bank Transfer
          if (
            detail.productType === "QIWI" &&
            includes(detail.productCurrencies, "RUB")
          ) {
            isRussianBankTransferPermitted.value = true;
          }

          // Permission is singular for both Ukraine and Israel transfers
          if (
            detail.productType === "R360" &&
            includes(detail.productCurrencies, "EUR")
          ) {
            /** NOTE: This product is for both UAH and ILS in
             * both Bank and Card transfer type */
            isUkraineBankTransferPermitted.value = true;
            isUkraineCardTransferPermitted.value = true;
            isIsraelBankTransferPermitted.value = true;
            isIsraelCardTransferPermitted.value = true;

            // push the allowed currency for Card Payments
            allowedCurrencyForCardTransfer.value.push(
              String(detail.productCurrencies)
            );
          }

          break;
        case AccountProducts.WITHDRAWAL_TO_CARD:
          // Specific values for Russian Card Transfer
          if (
            detail.productType === "QIWI" &&
            includes(detail.productCurrencies, "RUB")
          ) {
            isRussianCardTransferPermitted.value = true;

            // push the allowed currency for Card Payments
            allowedCurrencyForCardTransfer.value.push(
              String(detail.productCurrencies)
            );
          }
          break;
        case AccountProducts.CRYPTO_EXCHANGE_WITHDRAWAL:
          isCryptoWithdrawalPermitted.value = true;
          allowedFiatCurrenciesForCryptoWithdrawal.value =
            detail.productCurrencies ?? [];
          break;
        case AccountProducts.CRYPTO_EXCHANGE_DEPOSIT:
          isCryptoDepositPermitted.value = true;
          allowedFiatCurrenciesForCryptoDeposit.value =
            detail.productCurrencies ?? [];
          break;
        case AccountProducts.LOAD_ACCOUNT_BY_CARD:
          isCardAcquiringPermitted.value = true;
          // Currently there are two product types for Card Acquiring.
          // Check first if allowedFiatCurrenciesForCardAcquiring is empty
          if (isEmpty(allowedFiatCurrenciesForCardAcquiring.value)) {
            allowedFiatCurrenciesForCardAcquiring.value =
              detail.productCurrencies;
          } else {
            allowedFiatCurrenciesForCardAcquiring.value = union(
              allowedFiatCurrenciesForCardAcquiring.value,
              detail.productCurrencies
            );
          }
          break;
        case AccountProducts.EXCHANGE:
          isFiatExchangePermitted.value = true;
          allowedFiatCurrencyForFiatExchange.value =
            detail.productCurrencies ?? [];
          break;
        case AccountProducts.INCOMING_WIRE:
          // Case for Account Topup
          if (
            detail.productType === "CJSEPA" &&
            includes(detail.productCurrencies, "EUR")
          ) {
            isAllowedEEA.value = true;
          } else if (
            detail.productType === "CJFASTER" &&
            includes(detail.productCurrencies, "GBP")
          ) {
            isAllowedLocalGBP.value = true;
          } else if (detail.productType === "EXPRESS") {
            isAllowedInternational.value = true;
            allowedCurrencyForInternationalTopUp.value =
              detail.productCurrencies ?? [];
          }
          break;
      }
    });

    // Save values to Vuex Store
    await store.dispatch("accountPermission", {
      isCryptoWithdrawalPermitted,
      isCryptoDepositPermitted,
      // using computed property causes an error on logout
      isCryptoExchangePermitted:
        isCryptoWithdrawalPermitted.value || isCryptoDepositPermitted.value,
      allowedFiatCurrenciesForCryptoWithdrawal,
      allowedFiatCurrenciesForCryptoDeposit,
      isCardAcquiringPermitted,
      allowedFiatCurrenciesForCardAcquiring,
      isRussianBankTransferPermitted,
      isRussianCardTransferPermitted,
      isUkraineBankTransferPermitted,
      isUkraineCardTransferPermitted,
      isIsraelBankTransferPermitted,
      isIsraelCardTransferPermitted,
      isFiatExchangePermitted,
      allowedFiatCurrencyForFiatExchange,
      isAllowedEEA,
      isAllowedLocalGBP,
      isAllowedInternational,
      allowedCurrencyForInternationalTopUp,
      allowedCurrencyForCardTransfer,
    });
  });

  return {
    accountPermissionQuery,
    accountPermission,
    accountPermissionLoading: computed(
      () => accountPermissionQuery.loading.value
    ),
    fetchAccountPermission,
    isCryptoWithdrawalPermitted,
    isCryptoDepositPermitted,
    // using computed property causes an error on logout
    isCryptoExchangePermitted:
      isCryptoWithdrawalPermitted.value || isCryptoDepositPermitted.value,
    allowedFiatCurrenciesForCryptoWithdrawal,
    allowedFiatCurrenciesForCryptoDeposit,
    isCardAcquiringPermitted,
    allowedFiatCurrenciesForCardAcquiring,
    isRussianBankTransferPermitted,
    isRussianCardTransferPermitted,
    isUkraineBankTransferPermitted,
    isUkraineCardTransferPermitted,
    isIsraelBankTransferPermitted,
    isIsraelCardTransferPermitted,
    isFiatExchangePermitted,
    allowedFiatCurrencyForFiatExchange,
    isAllowedEEA,
    isAllowedLocalGBP,
    isAllowedInternational,
    allowedCurrencyForInternationalTopUp,
    allowedCurrencyForCardTransfer,
  };
}
